import type { CreateMessage, InsertMessageReaction, Message } from '@here/api'
import { userId } from '@state'
import { createResource, createSignal } from 'solid-js'
import { useService } from 'solid-services'
import { generateParentPreviewText } from './generateParentPreviewText'
import type { DisplayMessage } from './resources/getChannelHistory'
import { whenInsertMessage } from './resources/whenInsertMessage'
import { whenUpdateMessage } from './resources/whenUpdateMessage'
import { whenUpsertReaction } from './resources/whenUpsertReaction'
import { useUi } from './ui'

export function useUpsert() {
	const { setIsExpanded } = useService(useUi)()
	const [editingMessage, setEditingMessage] = createSignal<Message | undefined>()
	const [replyMessage, setReplyMessage] = createSignal<DisplayMessage | undefined>()

	const [upsertMessageReaction, setUpsertMessageReaction] =
		createSignal<InsertMessageReaction>()
	const [upsertMessageReactionResource] = createResource(
		upsertMessageReaction,
		whenUpsertReaction,
	)

	const [insertMessage, setInsertMessage] = createSignal<CreateMessage>()
	const [insertMessageResource] = createResource(insertMessage, whenInsertMessage)
	const [updateMessage, setUpdateMessage] = createSignal<Message>()
	const [updateMessageResource] = createResource(
		() => ({
			updateMessage: updateMessage(),
			editingMessage: editingMessage(),
		}),
		({ updateMessage, editingMessage }) =>
			whenUpdateMessage(updateMessage, editingMessage),
	)

	const send = (content: string, channel_id: string) => {
		const parent_id = replyMessage()?.id || null
		let thread_id = replyMessage()
			? (replyMessage()?.thread_id ?? replyMessage()?.id)
			: null
		thread_id = thread_id ?? null

		const parent_preview_text = replyMessage()?.content
			? generateParentPreviewText(replyMessage()!.content!)
			: null

		if (content !== '<p></p>' && content !== '') {
			if (editingMessage()) {
				setUpdateMessage({
					channel_id: editingMessage()!.channel_id,
					created_at: editingMessage()!.created_at,
					created_by: editingMessage()!.created_by,
					id: editingMessage()!.id,
					parent_preview_text,
					parent_id,
					thread_id,
					content,
				})
			} else {
				setInsertMessage({
					channel_id,
					created_by: userId()!,
					content,
					parent_preview_text,
					parent_id,
					thread_id,
				})
			}
		}
	}

	const onSuccesfulSend = () => {
		let performRefresh = false
		if (updateMessage()) {
			performRefresh = updateMessageResource.state === 'ready'
		}

		if (insertMessage()) {
			performRefresh = insertMessageResource.state === 'ready'
		}

		if (performRefresh) {
			setIsExpanded(false)
			setEditingMessage(undefined)
			setInsertMessage(undefined)
			setUpdateMessage(undefined)
			setReplyMessage(undefined)
		}

		return performRefresh
	}
	const sendIsLoading = () =>
		updateMessageResource.loading || insertMessageResource.loading

	return {
		editingMessage,
		insertMessageResource,
		onSuccesfulSend,
		replyMessage,
		send,
		sendIsLoading,
		setEditingMessage,
		setInsertMessage,
		setReplyMessage,
		setUpsertMessageReaction,
		updateMessageResource,
		upsertMessageReaction,
		upsertMessageReactionResource,
	}
}
